import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FolderOpen, CheckCircle, Clock, AlertCircle, Loader2,
  FileText, Pen, ChevronRight, DollarSign,
  Building2, Users, LayoutGrid, Download, MapPin, Phone, Mail,
  ScrollText, Briefcase, Receipt, ShieldCheck,
  ClipboardList,
} from 'lucide-react';
import {
  projectsApi, signingApi, billingApi, proposalsApi, meApi, reportsApi,
  authorizedFetch,
} from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import RateSheetView from '../components/RateSheetView';
import ContactList from '../components/ContactList';

const statusConfig = {
  active: { label: 'Active', color: '#22c55e', icon: Clock },
  in_progress: { label: 'In Progress', color: '#3b82f6', icon: Clock },
  pending_review: { label: 'Pending Review', color: '#f59e0b', icon: AlertCircle },
  completed: { label: 'Completed', color: '#22c55e', icon: CheckCircle },
  on_hold: { label: 'On Hold', color: '#6b7280', icon: AlertCircle },
};

const signingStatusConfig = {
  pending:   { label: 'Action Required', color: '#ef4444', bg: '#fef2f2' },
  partial:   { label: 'In Progress',     color: '#f59e0b', bg: '#fef3c7' },
  completed: { label: 'Completed',       color: '#22c55e', bg: '#dcfce7' },
  expired:   { label: 'Expired',         color: '#6b7280', bg: '#f3f4f6' },
};

const proposalStageLabels = {
  proposal:   { label: 'Awaiting Review',    color: '#1d4ed8', bg: '#dbeafe' },
  declined:   { label: 'Declined',           color: '#991b1b', bg: '#fee2e2' },
  contract:   { label: 'Ready to Sign',      color: '#b45309', bg: '#fef3c7' },
  onboarding: { label: 'Onboarding',         color: '#047857', bg: '#d1fae5' },
};

const invoiceStatusConfig = {
  draft:              { label: 'Draft',     color: '#6b7280', bg: '#f3f4f6' },
  ready:              { label: 'Ready',     color: '#1d4ed8', bg: '#dbeafe' },
  sent:               { label: 'Due',       color: '#a16207', bg: '#fef3c7' },
  past_due:           { label: 'Past Due',  color: '#b91c1c', bg: '#fee2e2' },
  partially_paid:     { label: 'Partial',   color: '#ea580c', bg: '#ffedd5' },
  auto_charge_failed: { label: 'Charge Failed', color: '#b91c1c', bg: '#fee2e2' },
  paid:               { label: 'Paid',      color: '#16a34a', bg: '#dcfce7' },
  voided:             { label: 'Voided',    color: '#6b7280', bg: '#f3f4f6' },
};

const TABS = [
  { id: 'overview',  label: 'Overview',  icon: LayoutGrid },
  { id: 'rates',     label: 'Rates',     icon: DollarSign },
  { id: 'invoices',  label: 'Invoices',  icon: Receipt, billingOnly: true },
  { id: 'documents', label: 'Documents', icon: ScrollText },
  { id: 'contacts',  label: 'Contacts',  icon: Users },
  { id: 'projects',  label: 'Projects',  icon: Briefcase },
  { id: 'proposals', label: 'Proposals', icon: FileText },
];

function getProgressPercent(completed, total) {
  if (!total) return 0;
  return Math.round((completed / total) * 100);
}

function scoreColor(score) {
  if (score == null) return '#6b7280';
  if (score >= 85) return '#16a34a';
  if (score >= 70) return '#068dac';
  if (score >= 50) return '#d97706';
  return '#dc2626';
}

function formatAddress(c) {
  if (!c) return '';
  const line2 = [c.city, c.state].filter(Boolean).join(', ');
  const tail = [line2, c.zip].filter(Boolean).join(' ');
  return [c.address, tail].filter(Boolean).join(' • ');
}

function planSummary(rateSheet) {
  if (!rateSheet?.entries?.length) return null;
  // Pick the first MSP-like entry, otherwise the first entry. The intent
  // is to surface the headline plan ("MSP Managed Services — Limited
  // Managed, 3 hrs included @ $150/hr") on the Overview tab without
  // hard-coding service-type names.
  const msp = rateSheet.entries.find((e) =>
    /msp/i.test(e.service_type_name || '')
  ) || rateSheet.entries[0];
  if (!msp) return null;
  const fields = msp.rate_fields || [];
  const vals = msp.rate_values || {};
  // Pull a few headline values heuristically — plan tier, included hours,
  // hourly rate. Falls back to the first 3 visible fields.
  const headlineKeys = ['plan_tier', 'included_hours', 'hourly_rate', 'rate'];
  const headlineFields = fields.filter((f) =>
    headlineKeys.includes(f.key) && vals[f.key] != null && vals[f.key] !== ''
  );
  const useFields = headlineFields.length
    ? headlineFields
    : fields
        .filter(
          (f) =>
            (!f.show_when ||
              (f.show_when.equals !== undefined &&
                vals[f.show_when.field] === f.show_when.equals)) &&
            vals[f.key] != null &&
            vals[f.key] !== ''
        )
        .slice(0, 3);
  return {
    serviceName: msp.service_type_name,
    fields: useFields.map((f) => ({
      label: f.label,
      value: formatRateValue(vals[f.key], f),
    })),
  };
}

function formatRateValue(value, fieldDef) {
  if (value == null || value === '') return '—';
  // Tier arrays — [{upto, pct}] — render as a readable schedule instead of
  // "[object Object]". e.g. "7%" or "7% up to $10,000, 5%".
  if (Array.isArray(value)) {
    const parts = value
      .map((t) => {
        if (t == null || typeof t !== 'object') return String(t ?? '');
        const pct = t.pct != null && t.pct !== '' ? `${t.pct}%` : '';
        const cap = t.upto != null && t.upto !== ''
          ? ` up to $${Number(t.upto).toLocaleString()}`
          : '';
        return `${pct}${cap}`.trim();
      })
      .filter(Boolean);
    return parts.length ? parts.join(', ') : '—';
  }
  // Never render a raw object as "[object Object]".
  if (typeof value === 'object') return '—';
  switch (fieldDef.type) {
    case 'currency':
      return `$${Number(value).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    case 'percentage':
      return `${value}%`;
    default:
      return String(value);
  }
}

const Dashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [activeTab, setActiveTab] = useState('overview');

  const [projects, setProjects] = useState([]);
  const [signingPackages, setSigningPackages] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [servicePlans, setServicePlans] = useState([]); // remediation proposals
  const [customer, setCustomer] = useState(null);
  const [rateSheet, setRateSheet] = useState({ sheet: null, entries: [] });
  const [documents, setDocuments] = useState([]);

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [billingEnabled, setBillingEnabled] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [staffDocs, setStaffDocs] = useState([]);
  const [latestReport, setLatestReport] = useState(null);

  useEffect(() => {
    loadAll();
    // Newest security assessment — surfaced as a hero card on the overview.
    // Best-effort; the card just doesn't render if there's none.
    reportsApi.list()
      .then((d) => { const l = (d && d.reports) || []; if (l.length) setLatestReport(l[0]); })
      .catch(() => {});
    // Files staff uploaded for this customer with client_accessible=true.
    // Best-effort — empty list on failure.
    meApi.getStaffDocuments().then(setStaffDocs).catch(() => setStaffDocs([]));
    billingApi.isEnabled().then(async (enabled) => {
      setBillingEnabled(enabled);
      if (enabled) {
        try {
          const list = await billingApi.listInvoices();
          setInvoices(list || []);
        } catch {
          setInvoices([]);
        }
      }
    });
  }, []);

  const loadAll = async () => {
    try {
      const [
        projectData, signingData, proposalData,
        customerData, rateSheetData, documentsData,
        servicePlanData,
      ] = await Promise.allSettled([
        projectsApi.getProjects(),
        signingApi.getMyPackages(),
        proposalsApi.list(),
        meApi.getCustomer(),
        meApi.getRateSheet(),
        meApi.getDocuments(),
        authorizedFetch('/api/remediation/portal/plans').then(r => (r.ok ? r.json() : [])),
      ]);

      if (projectData.status === 'fulfilled') setProjects(projectData.value);
      if (signingData.status === 'fulfilled') setSigningPackages(signingData.value);
      if (proposalData.status === 'fulfilled') setProposals(proposalData.value || []);
      if (servicePlanData.status === 'fulfilled') setServicePlans(servicePlanData.value || []);
      if (customerData.status === 'fulfilled') setCustomer(customerData.value);
      if (rateSheetData.status === 'fulfilled') setRateSheet(rateSheetData.value);
      if (documentsData.status === 'fulfilled') setDocuments(documentsData.value || []);
    } catch (err) {
      setError('Failed to load your information. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const pendingSignatures = signingPackages.filter(
    (p) => p.package_status === 'pending' || p.package_status === 'partial'
  );
  const actionableProposals = proposals.filter(
    (p) =>
      p.current_stage === 'proposal' ||
      p.current_stage === 'declined' ||
      p.current_stage === 'contract'
  );
  const actionableServicePlans = servicePlans.filter((p) => p.status === 'sent');
  const activeProjects = projects.filter((p) => p.status !== 'completed');

  // Flatten all signed documents from /me/documents into a list for the
  // Documents tab. Pending docs still get rendered grouped under their
  // package elsewhere via the existing signing-card UI.
  const signedDocuments = useMemo(() => {
    const out = [];
    documents.forEach((pkg) => {
      (pkg.documents || []).forEach((d) => {
        if (d.signing_status === 'signed') {
          out.push({
            ...d,
            package_id: pkg.package_id,
            package_status: pkg.package_status,
            customer_name: pkg.customer_name,
          });
        }
      });
    });
    return out.sort((a, b) => {
      const ta = a.signed_at ? Date.parse(a.signed_at) : 0;
      const tb = b.signed_at ? Date.parse(b.signed_at) : 0;
      return tb - ta;
    });
  }, [documents]);

  const overviewPlan = planSummary(rateSheet);
  const primaryContact =
    customer?.contacts?.find((c) => c.is_primary_contact) ||
    customer?.contacts?.[0] ||
    null;

  if (isLoading) {
    return (
      <div className="dashboard-loading">
        <Loader2 className="spinner-large" size={48} />
        <p>Loading your portal...</p>
      </div>
    );
  }

  return (
    <div className="dashboard">

      <main className="dashboard-content">
        <div className="welcome-section">
          <h2>
            Welcome back
            {customer?.name
              ? `, ${customer.name}`
              : user?.customer_name
              ? `, ${user.customer_name}`
              : ''}
          </h2>
          <p>
            Your company info, rates, signed documents, and active work all
            live here.
          </p>
        </div>

        {error && <div className="error-message">{error}</div>}

        {/* ── TAB BAR ── */}
        <div className="portal-tab-bar">
          {TABS.filter(t => !t.billingOnly || billingEnabled).map((t) => {
            const Icon = t.icon;
            const isActive = activeTab === t.id;
            return (
              <button
                key={t.id}
                className={`portal-tab ${isActive ? 'active' : ''}`}
                onClick={() => setActiveTab(t.id)}
                type="button"
              >
                <Icon size={16} />
                <span>{t.label}</span>
              </button>
            );
          })}
        </div>

        {/* ── OVERVIEW ── */}
        {activeTab === 'overview' && (
          <div className="portal-tab-content">
            {/* Security assessment hero — the first thing a client sees on login */}
            {latestReport && (
              <button
                type="button"
                className="portal-overview-card"
                onClick={() => navigate(`/report/${latestReport.id}`)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 16, width: '100%',
                  textAlign: 'left', cursor: 'pointer', marginBottom: 16,
                  borderLeft: `4px solid ${scoreColor(latestReport.score)}`,
                }}
              >
                <div style={{
                  width: 62, height: 62, borderRadius: 12, flexShrink: 0,
                  background: scoreColor(latestReport.score), color: '#fff',
                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                }}>
                  <span style={{ fontSize: 24, fontWeight: 800, lineHeight: 1 }}>
                    {latestReport.score != null ? latestReport.score : '—'}
                  </span>
                  <span style={{ fontSize: 9, opacity: 0.9 }}>/ 100</span>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <ShieldCheck size={18} color={scoreColor(latestReport.score)} />
                    <h3 style={{ margin: 0, fontSize: 16 }}>Your security assessment</h3>
                    {!latestReport.viewed && (
                      <span style={{
                        background: '#ef4444', color: '#fff', fontSize: 10, fontWeight: 700,
                        padding: '2px 8px', borderRadius: 10, letterSpacing: '.03em',
                      }}>NEW</span>
                    )}
                  </div>
                  <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: 13 }}>
                    {latestReport.band ? `${latestReport.band} — ` : ''}
                    updated {latestReport.generated_at ? new Date(latestReport.generated_at).toLocaleDateString() : 'recently'}.
                    View your latest posture, findings, and recommendations.
                  </p>
                </div>
                <ChevronRight size={20} color="#94a3b8" style={{ flexShrink: 0 }} />
              </button>
            )}

            <div className="portal-overview-grid">
              {/* Company card */}
              <div className="portal-overview-card">
                <div className="portal-overview-card-header">
                  <Building2 size={18} />
                  <h3>{customer?.name || 'Your company'}</h3>
                </div>
                <div className="portal-overview-card-body">
                  {customer?.address || customer?.city ? (
                    <div className="portal-overview-row">
                      <MapPin size={14} />
                      <span>{formatAddress(customer)}</span>
                    </div>
                  ) : null}
                  {customer?.phone && (
                    <div className="portal-overview-row">
                      <Phone size={14} />
                      <span>{customer.phone}</span>
                    </div>
                  )}
                  {!customer?.address && !customer?.phone && (
                    <p className="portal-muted">
                      Company contact info isn't on file yet.
                    </p>
                  )}
                </div>
              </div>

              {/* Primary contact card */}
              <div className="portal-overview-card">
                <div className="portal-overview-card-header">
                  <Users size={18} />
                  <h3>Primary contact</h3>
                </div>
                <div className="portal-overview-card-body">
                  {primaryContact ? (
                    <>
                      <div className="portal-overview-name">
                        {[primaryContact.first_name, primaryContact.last_name]
                          .filter(Boolean)
                          .join(' ')}
                      </div>
                      {primaryContact.title && (
                        <div className="portal-muted">{primaryContact.title}</div>
                      )}
                      {primaryContact.email && (
                        <div className="portal-overview-row">
                          <Mail size={14} />
                          <a href={`mailto:${primaryContact.email}`}>
                            {primaryContact.email}
                          </a>
                        </div>
                      )}
                      {primaryContact.phone && (
                        <div className="portal-overview-row">
                          <Phone size={14} />
                          <a href={`tel:${primaryContact.phone}`}>
                            {primaryContact.phone}
                          </a>
                        </div>
                      )}
                    </>
                  ) : (
                    <p className="portal-muted">No contacts on file.</p>
                  )}
                </div>
              </div>
            </div>

            {/* Stat cards */}
            <div className="portal-stat-grid">
              <button
                className="portal-stat-card"
                onClick={() => setActiveTab('projects')}
                type="button"
              >
                <div className="portal-stat-value">{activeProjects.length}</div>
                <div className="portal-stat-label">Active Projects</div>
              </button>
              <button
                className="portal-stat-card"
                onClick={() => setActiveTab('documents')}
                type="button"
              >
                <div className="portal-stat-value">{signedDocuments.length}</div>
                <div className="portal-stat-label">Signed Documents</div>
              </button>
              <button
                className="portal-stat-card"
                onClick={() => setActiveTab('proposals')}
                type="button"
              >
                <div className="portal-stat-value">
                  {actionableProposals.length}
                </div>
                <div className="portal-stat-label">Active Proposals</div>
              </button>
            </div>

            {/* Current plan summary */}
            {overviewPlan && (
              <div className="portal-overview-card portal-plan-card">
                <div className="portal-overview-card-header">
                  <DollarSign size={18} />
                  <h3>Current plan</h3>
                  <button
                    type="button"
                    className="portal-plan-link"
                    onClick={() => setActiveTab('rates')}
                  >
                    View all rates <ChevronRight size={14} />
                  </button>
                </div>
                <div className="portal-overview-card-body">
                  <div className="portal-plan-service">
                    {overviewPlan.serviceName}
                  </div>
                  <div className="portal-plan-fields">
                    {overviewPlan.fields.map((f) => (
                      <div key={f.label} className="portal-plan-field">
                        <span className="portal-plan-field-label">
                          {f.label}
                        </span>
                        <span className="portal-plan-field-value">
                          {f.value}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Action callouts when there are urgent items */}
            {(pendingSignatures.length > 0 ||
              actionableProposals.length > 0 ||
              actionableServicePlans.length > 0) && (
              <div className="portal-callouts">
                {(actionableProposals.length + actionableServicePlans.length) > 0 && (
                  <button
                    type="button"
                    className="portal-callout"
                    onClick={() => setActiveTab('proposals')}
                  >
                    <AlertCircle size={16} />
                    <span>
                      {actionableProposals.length + actionableServicePlans.length} proposal
                      {(actionableProposals.length + actionableServicePlans.length) === 1 ? '' : 's'} need
                      {(actionableProposals.length + actionableServicePlans.length) === 1 ? 's' : ''} your review
                    </span>
                    <ChevronRight size={14} />
                  </button>
                )}
                {pendingSignatures.length > 0 && (
                  <button
                    type="button"
                    className="portal-callout"
                    onClick={() => setActiveTab('documents')}
                  >
                    <Pen size={16} />
                    <span>
                      {pendingSignatures.length} document
                      {pendingSignatures.length === 1 ? '' : 's'} awaiting your
                      signature
                    </span>
                    <ChevronRight size={14} />
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {/* ── RATES ── */}
        {activeTab === 'rates' && (
          <div className="portal-tab-content">
            <RateSheetView
              sheet={rateSheet?.sheet}
              entries={rateSheet?.entries}
            />
          </div>
        )}

        {/* ── INVOICES ── */}
        {activeTab === 'invoices' && billingEnabled && (
          <div className="portal-tab-content">
            <div className="portal-overview-card">
              <div className="portal-overview-card-header">
                <Receipt size={16} />
                <h3>Invoices</h3>
                <button
                  className="portal-link-btn"
                  type="button"
                  onClick={() => navigate('/billing')}
                  style={{ marginLeft: 'auto' }}
                >
                  Open billing center →
                </button>
              </div>
              <div className="portal-overview-card-body">
                {invoices.length === 0 ? (
                  <div className="empty-messages">No invoices yet.</div>
                ) : (
                  <table className="portal-invoices-table">
                    <thead>
                      <tr>
                        <th>Invoice</th>
                        <th>Issued</th>
                        <th>Due</th>
                        <th style={{ textAlign: 'right' }}>Amount</th>
                        <th>Status</th>
                        <th style={{ textAlign: 'right' }}>PDF</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoices.map((inv) => {
                        const c = invoiceStatusConfig[inv.status] || {
                          label: inv.status, color: '#64748b', bg: '#f1f5f9',
                        };
                        return (
                          <tr key={inv.id}>
                            <td><strong>{inv.invoice_number}</strong></td>
                            <td>{(inv.issue_date || '').slice(0, 10)}</td>
                            <td>{(inv.due_date || '').slice(0, 10)}</td>
                            <td style={{ textAlign: 'right' }}>
                              ${(Number(inv.total) || 0).toFixed(2)}
                            </td>
                            <td>
                              <span
                                className="invoice-status-pill"
                                style={{ color: c.color, background: c.bg }}
                              >
                                {c.label}
                              </span>
                            </td>
                            <td style={{ textAlign: 'right' }}>
                              <button
                                type="button"
                                className="link-btn"
                                onClick={() =>
                                  billingApi.openInvoicePdf(inv.id).catch((e) => alert(e.message))
                                }
                                title="Download PDF"
                              >
                                <Download size={14} />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ── DOCUMENTS ── */}
        {activeTab === 'documents' && (
          <div className="portal-tab-content">
            {/* Pending packages — needs action */}
            {pendingSignatures.length > 0 && (
              <section className="dashboard-section">
                <div className="section-header">
                  <Pen size={20} />
                  <h3>Awaiting your signature</h3>
                  <span className="section-badge urgent">
                    {pendingSignatures.length} need
                    {pendingSignatures.length === 1 ? 's' : ''} signature
                  </span>
                </div>
                <div className="signing-cards">
                  {pendingSignatures.map((pkg) => {
                    const sc =
                      signingStatusConfig[pkg.package_status] ||
                      signingStatusConfig.pending;
                    const progress = getProgressPercent(
                      pkg.signed_docs,
                      pkg.total_docs
                    );
                    return (
                      <div
                        key={pkg.id}
                        className={`signing-card ${pkg.package_status}`}
                        onClick={() => (window.location.href = pkg.sign_url)}
                        style={{ cursor: 'pointer' }}
                      >
                        <div className="signing-card-header">
                          <div className="signing-card-title">
                            <FileText size={18} color={sc.color} />
                            <span>
                              {pkg.customer_name} — Service Agreements
                            </span>
                          </div>
                          <span
                            className="signing-status-pill"
                            style={{ color: sc.color, background: sc.bg }}
                          >
                            {sc.label}
                          </span>
                        </div>
                        <div className="signing-card-progress">
                          <div className="progress-header">
                            <span>
                              {pkg.signed_docs} of {pkg.total_docs} documents
                              signed
                            </span>
                          </div>
                          <div className="progress-bar">
                            <div
                              className="progress-fill"
                              style={{
                                width: `${progress}%`,
                                background: sc.color,
                              }}
                            />
                          </div>
                        </div>
                        <div className="signing-card-action">
                          <Pen size={14} />
                          <span>Review &amp; Sign</span>
                          <ChevronRight size={14} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            )}

            {/* Files shared by your team (customer_documents.client_accessible) */}
            {staffDocs.length > 0 && (
              <section className="dashboard-section">
                <div className="section-header">
                  <FileText size={20} />
                  <h3>Files from your team</h3>
                </div>
                <div className="portal-document-list">
                  {staffDocs.map((d) => (
                    <div key={d.id} className="portal-document-row">
                      <div className="portal-document-info">
                        <FileText size={18} color="#3b82f6" />
                        <div>
                          <div className="portal-document-name">{d.file_name}</div>
                          <div className="portal-document-meta">
                            {d.description ? `${d.description} · ` : ''}
                            {d.created_at ? new Date(d.created_at).toLocaleDateString() : ''}
                          </div>
                        </div>
                      </div>
                      {d.download_url ? (
                        <a className="portal-document-download" href={d.download_url} target="_blank" rel="noopener noreferrer">
                          <Download size={14} /> Download
                        </a>
                      ) : (
                        <span style={{ fontSize: 12, color: '#94a3b8' }}>Link unavailable</span>
                      )}
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Signed documents list */}
            <section className="dashboard-section">
              <div className="section-header">
                <ScrollText size={20} />
                <h3>Signed documents</h3>
              </div>
              {signedDocuments.length === 0 ? (
                <div className="empty-state">
                  <ScrollText size={48} className="empty-icon" />
                  <h3>No signed documents yet</h3>
                  <p>
                    Once you've signed your agreements they'll be available to
                    download here.
                  </p>
                </div>
              ) : (
                <div className="portal-document-list">
                  {signedDocuments.map((d) => (
                    <div key={d.id} className="portal-document-row">
                      <div className="portal-document-info">
                        <FileText size={18} color="#22c55e" />
                        <div>
                          <div className="portal-document-name">
                            {d.document_name || 'Document'}
                          </div>
                          <div className="portal-document-meta">
                            Signed{' '}
                            {d.signed_at
                              ? new Date(d.signed_at).toLocaleDateString()
                              : '—'}
                          </div>
                        </div>
                      </div>
                      {d.download_url ? (
                        <a
                          href={d.download_url}
                          className="portal-document-download"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <Download size={14} /> Download
                        </a>
                      ) : (
                        <span className="portal-muted portal-document-meta">
                          Download unavailable
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </section>
          </div>
        )}

        {/* ── CONTACTS ── */}
        {activeTab === 'contacts' && (
          <div className="portal-tab-content">
            <ContactList contacts={customer?.contacts || []} />
          </div>
        )}

        {/* ── PROJECTS ── */}
        {activeTab === 'projects' && (
          <div className="portal-tab-content">
            {projects.length === 0 ? (
              <div className="empty-state">
                <FolderOpen size={48} className="empty-icon" />
                <h3>No projects yet</h3>
                <p>
                  When your account team kicks off a project for you, it'll
                  show up here.
                </p>
              </div>
            ) : (
              <div className="projects-grid">
                {projects.map((project) => {
                  const status =
                    statusConfig[project.status] || statusConfig.active;
                  const StatusIcon = status.icon;
                  const progress = getProgressPercent(
                    project.completed_questions,
                    project.total_questions
                  );

                  return (
                    <div
                      key={project.id}
                      className="project-card"
                      onClick={() => navigate(`/projects/${project.id}`)}
                    >
                      <div className="project-header">
                        <h3 className="project-name">{project.name}</h3>
                        <span className="project-type">
                          {project.project_type}
                        </span>
                      </div>

                      <div
                        className="project-status"
                        style={{ color: status.color }}
                      >
                        <StatusIcon size={16} />
                        {status.label}
                      </div>

                      <div className="project-progress">
                        <div className="progress-header">
                          <span>Questions Completed</span>
                          <span>
                            {project.completed_questions} /{' '}
                            {project.total_questions}
                          </span>
                        </div>
                        <div className="progress-bar">
                          <div
                            className="progress-fill"
                            style={{ width: `${progress}%` }}
                          />
                        </div>
                      </div>

                      {project.pending_review > 0 && (
                        <div className="pending-badge">
                          <AlertCircle size={14} />
                          {project.pending_review} pending review
                        </div>
                      )}

                      <div className="project-footer">
                        <span className="created-date">
                          Created{' '}
                          {new Date(project.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ── PROPOSALS ── */}
        {activeTab === 'proposals' && (
          <div className="portal-tab-content">
            {proposals.length === 0 && servicePlans.length === 0 ? (
              <div className="empty-state">
                <FileText size={48} className="empty-icon" />
                <h3>No active proposals</h3>
                <p>You don't have any proposals waiting for review right now.</p>
              </div>
            ) : (
              <div className="signing-cards">
                {/* Service (remediation) proposals — same list, own detail flow */}
                {servicePlans.map((p) => (
                  <div
                    key={p.id}
                    className="signing-card"
                    onClick={() => navigate(`/service-proposals/${p.id}`)}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="signing-card-header">
                      <div className="signing-card-title">
                        <ClipboardList size={18} color={p.status === 'sent' ? '#3182ce' : '#137333'} />
                        <span>{p.title}</span>
                      </div>
                      <span
                        className="signing-status-pill"
                        style={p.status === 'sent'
                          ? { color: '#1a56a8', background: '#e8f0fe' }
                          : { color: '#137333', background: '#e6f4ea' }}
                      >
                        {p.status === 'sent' ? 'Awaiting your review' : 'Responded'}
                      </span>
                    </div>
                    <div className="signing-card-action">
                      <ChevronRight size={14} />
                      <span>{p.status === 'sent' ? 'Review & approve items' : 'View your selections'}</span>
                    </div>
                  </div>
                ))}
                {proposals.map((p) => {
                  const sc =
                    proposalStageLabels[p.current_stage] ||
                    proposalStageLabels.proposal;
                  const target =
                    p.current_stage === 'contract' ||
                    p.current_stage === 'onboarding'
                      ? `/contract/${p.id}`
                      : `/proposal/${p.id}`;
                  return (
                    <div
                      key={p.id}
                      className="signing-card"
                      onClick={() => navigate(target)}
                      style={{ cursor: 'pointer' }}
                    >
                      <div className="signing-card-header">
                        <div className="signing-card-title">
                          <FileText size={18} color={sc.color} />
                          <span>
                            {p.customer_name} —{' '}
                            {({ msp: 'MSP', insurance: 'Insurance', construction: 'Construction', hardware: 'Hardware', compliance: 'Compliance' }[p.path_type]) || 'Insurance'}{' '}
                            Proposal
                          </span>
                        </div>
                        <span
                          className="signing-status-pill"
                          style={{ color: sc.color, background: sc.bg }}
                        >
                          {sc.label}
                        </span>
                      </div>
                      <div className="signing-card-action">
                        <ChevronRight size={14} />
                        <span>
                          {p.current_stage === 'proposal' && 'Review proposal'}
                          {p.current_stage === 'declined' &&
                            'Reconsider proposal'}
                          {p.current_stage === 'contract' &&
                            'Continue to signing'}
                          {p.current_stage === 'onboarding' &&
                            'Continue onboarding'}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default Dashboard;
